/**
 * Shared Constants for AstrologyChart System
 * 
 * This module provides centralized constants used across the entire astrology chart system.
 * Following DRY principles, all magic numbers and mappings are defined here once.
 */

import { RasiName } from '../../../../models/types';
/**
 * Mapping from Rasi names to box numbers in D1 layout
 * Traditional North Indian layout: [12][1][2][3] / [11][4] / [10][5] / [9][8][7][6]
 */
export const RASHI_BOX_MAPPING: Record<RasiName, number> = {
  [RasiName.Mesha]: 1,      // Container-1 (API rasi_number: 1)
  [RasiName.Vrishabha]: 2,  // Container-2 (API rasi_number: 2)
  [RasiName.Mithuna]: 3,    // Container-3 (API rasi_number: 3)
  [RasiName.Karka]: 4,      // Container-4 (API rasi_number: 4)
  [RasiName.Simha]: 5,      // Container-5 (API rasi_number: 5)
  [RasiName.Kanya]: 6,      // Container-6 (API rasi_number: 6)
  [RasiName.Tula]: 7,       // Container-7 (API rasi_number: 7)
  [RasiName.Vrishchika]: 8, // Container-8 (API rasi_number: 8)
  [RasiName.Dhanu]: 9,      // Container-9 (API rasi_number: 9)
  [RasiName.Makara]: 10,    // Container-10 (API rasi_number: 10)
  [RasiName.Kumbha]: 11,    // Container-11 (API rasi_number: 11)
  [RasiName.Meena]: 0       // Container-0 (API rasi_number: 12)
} as const;

/**
 * Reverse mapping from box numbers to Rasi names
 * Automatically generated from RASHI_BOX_MAPPING to ensure consistency
 */
export const BOX_RASHI_MAPPING: Record<number, RasiName> = Object.fromEntries(
  Object.entries(RASHI_BOX_MAPPING).map(([rasi, box]) => [box, rasi as RasiName])
) as Record<number, RasiName>;

/**
 * Chart layout constants
 */
export const CHART_CONSTANTS = {
  /** Total number of rashi boxes in standard chart */
  RASHI_BOX_COUNT: 12,
  
  /** Valid box number range for rashi boxes (0-11 for container IDs) */
  RASHI_BOX_RANGE: { MIN: 0, MAX: 11 } as const,
  
  /** Degree range within each rashi */
  DEGREE_RANGE: { MIN: 0, MAX: 30 } as const,
  
  /** Default chart dimensions for 4x4 zodiac grid */
  CHART_DIMENSIONS: {
    DEFAULT_WIDTH: 784,
    DEFAULT_HEIGHT: 784,
    CELL_SIZE_MULTIPLIER: 2,
    GRID_SIZE: 4 // 4x4 grid for zodiac chart
  } as const,
  
  /** Animation and transition durations */
  TRANSITIONS: {
    ZOOM_DURATION: 100,
    LAYOUT_SWITCH_DURATION: 150,
    HOVER_DURATION: 50
  } as const,
  
  /** Performance optimization constants */
  PERFORMANCE: {
    ZOOM_UPDATE_THRESHOLD: 0.05,
    RESIZE_DEBOUNCE_MS: 150,
    ANIMATION_FRAME_BUDGET_MS: 16 // 60fps
  } as const
} as const;



/**
 * Error and validation constants
 */
export const VALIDATION_CONSTANTS = {
  /** Error messages */
  ERRORS: {
    INVALID_BOX_NUMBER: 'Box number must be between 1 and 12',
    INVALID_RASHI: 'Invalid rashi name provided',
    INVALID_ZOOM_LEVEL: 'Zoom level must be between min and max values',
    MISSING_LAYOUT_DATA: 'Layout data is required',
    COMPONENT_NOT_INITIALIZED: 'Component must be initialized before use'
  } as const,
  
  /** Validation rules */
  RULES: {
    MIN_BOX_NAME_LENGTH: 1,
    MAX_BOX_NAME_LENGTH: 10,
    MIN_FONT_SIZE: 4,
    MAX_FONT_SIZE: 72
  } as const
} as const;

/**
 * Helper function to validate rashi box numbers
 */
export function isValidRashiBox(boxNumber: number): boolean {
  return Number.isInteger(boxNumber) && 
         boxNumber >= CHART_CONSTANTS.RASHI_BOX_RANGE.MIN && 
         boxNumber <= CHART_CONSTANTS.RASHI_BOX_RANGE.MAX;
}
